import type { DeliveryNoteSummary, DocumentTemplateKey, InvoiceSummary, OrderDetails, ProductionReport, ShopAccount, StatementReport } from "@platform/contracts";
import { formatDate, formatDateTime } from "./dates";

export interface DeliveryNoteEntry { note: DeliveryNoteSummary; order: OrderDetails; shop?: ShopAccount }
export interface InvoiceEntry { invoice: InvoiceSummary; orders: OrderDetails[]; shop?: ShopAccount }

export function escapeHtml(value: string): string { return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" } as Record<string, string>)[c]!); }

export function openPrintWindow(html: string): boolean {
  const win = window.open("", "_blank");
  if (!win) return false;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.onload = () => win.print();
  return true;
}

/** Minimal mustache-style renderer: {{field}}, {{#list}}…{{/list}}, {{^field}}…{{/field}}. Values are HTML-escaped unless the key starts with "&". */
export function renderTemplate(template: string, data: Record<string, unknown>): string {
  const sections = /\{\{([#^])\s*([\w.]+)\s*\}\}([\s\S]*?)\{\{\/\s*\2\s*\}\}/g;
  let output = template;
  let previous = "";
  while (output !== previous) {
    previous = output;
    output = output.replace(sections, (_match, kind: string, key: string, body: string) => {
      const value = resolve(data, key);
      if (kind === "^") return value === undefined || value === null || value === false || value === "" || (Array.isArray(value) && !value.length) ? renderTemplate(body, data) : "";
      if (Array.isArray(value)) return value.map((item, index) => renderTemplate(body, { ...data, ...(item && typeof item === "object" ? item as Record<string, unknown> : { ".": item }), index: index + 1 })).join("");
      if (!value) return "";
      return renderTemplate(body, value && typeof value === "object" ? { ...data, ...value as Record<string, unknown> } : data);
    });
  }
  return output.replace(/\{\{(&?)\s*([\w.]+)\s*\}\}/g, (_match, raw: string, key: string) => {
    const value = resolve(data, key);
    if (value === undefined || value === null) return "";
    return raw === "&" ? String(value) : escapeHtml(String(value));
  });
}
function resolve(data: Record<string, unknown>, key: string): unknown {
  return key.split(".").reduce<unknown>((current, part) => (current && typeof current === "object" ? (current as Record<string, unknown>)[part] : undefined), data);
}

const BASE_STYLES = `
  body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0}
  .page{padding:28px;page-break-after:always}
  .page:last-child{page-break-after:auto}
  .doc-header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:10px}
  .doc-header h1{margin:0;font-size:20px;text-transform:uppercase;letter-spacing:1px}
  .doc-number{font-size:13px;color:#444}
  .brand{display:flex;align-items:center;gap:10px;font-weight:800;font-size:18px;letter-spacing:-0.5px}
  .brand img{height:38px;width:auto}
  .meta{display:flex;gap:40px;margin:16px 0;font-size:12px}
  .meta div{display:flex;flex-direction:column;gap:2px}
  .meta small{color:#666;margin-top:6px}
  .meta small:first-child{margin-top:0}
  table{width:100%;border-collapse:collapse;margin-top:8px}
  th,td{border:1px solid #ccc;padding:6px 8px;font-size:12px;text-align:left}
  th{background:#f2f2f2}
  td.num,th.num{text-align:right}
  tfoot td{font-weight:bold}
  .notes{font-size:12px;margin-top:12px}
  .trays{display:flex;flex-direction:column;gap:2px;margin-top:14px;padding:10px 12px;border:1px solid #111;border-radius:4px;font-size:12px}
  .trays span{color:#555;font-size:11px}
  .signatures{display:flex;gap:32px;margin-top:44px}
  .signatures div{flex:1;display:flex;flex-direction:column;gap:24px;font-size:11px;color:#555}
  .signatures i{border-bottom:1px solid #999;display:block}
`;

export const DEFAULT_TEMPLATES: Record<DocumentTemplateKey, string> = {
  delivery_note: `<section class="page">
  <div class="doc-header">
    <div><h1>Delivery note</h1><strong class="doc-number">{{number}}</strong></div>
    <div class="brand">{{#logoUrl}}<img src="{{logoUrl}}" alt="">{{/logoUrl}}<span>Tal-Ahwa</span></div>
  </div>
  <div class="meta">
    <div><small>Deliver to</small><strong>{{shopName}}</strong>{{#shopAddress}}<span>{{shopAddress}}</span>{{/shopAddress}}{{#vatNumber}}<span>VAT: {{vatNumber}}</span>{{/vatNumber}}{{#clientName}}<span>{{clientName}}</span>{{/clientName}}</div>
    <div><small>Issued</small><strong>{{issuedDate}}</strong><small>Order reference</small><strong>{{orderNumber}}</strong><small>Order date</small><span>{{orderDate}}</span></div>
  </div>
  <table>
    <thead><tr><th>#</th><th>SKU</th><th>Product</th><th class="num">Quantity</th></tr></thead>
    <tbody>{{#items}}<tr><td>{{index}}</td><td>{{sku}}</td><td>{{productName}}</td><td class="num">{{quantity}}</td></tr>{{/items}}</tbody>
    <tfoot><tr><td colspan="3">Total items</td><td class="num">{{totalQuantity}}</td></tr></tfoot>
  </table>
  {{#notes}}<p class="notes"><small>Notes:</small> {{notes}}</p>{{/notes}}
  {{#trayCount}}<p class="trays"><strong>Trays provided: {{trayCount}}</strong><span>To be collected on the next delivery.</span></p>{{/trayCount}}
  <div class="signatures"><div><span>Delivered by</span><i></i></div><div><span>Received by</span><i></i></div><div><span>Date</span><i></i></div></div>
</section>`,
  invoice: `<section class="page">
  <div class="doc-header">
    <div><h1>Invoice</h1><strong class="doc-number">{{number}}</strong></div>
    <div class="brand">{{#logoUrl}}<img src="{{logoUrl}}" alt="">{{/logoUrl}}<span>Tal-Ahwa</span></div>
  </div>
  <div class="meta">
    <div><small>Bill to</small><strong>{{shopName}}</strong>{{#shopAddress}}<span>{{shopAddress}}</span>{{/shopAddress}}{{#vatNumber}}<span>VAT: {{vatNumber}}</span>{{/vatNumber}}{{#clientName}}<span>{{clientName}}</span>{{/clientName}}</div>
    <div><small>Issued</small><strong>{{issuedDate}}</strong>{{#dueDate}}<small>Due</small><strong>{{dueDate}}</strong>{{/dueDate}}<small>Order reference</small><span>{{orderNumber}}</span></div>
  </div>
  <table>
    <thead><tr><th>#</th><th>SKU</th><th>Product</th><th class="num">Qty</th><th class="num">Unit price</th><th class="num">Line total</th></tr></thead>
    <tbody>{{#items}}<tr><td>{{index}}</td><td>{{sku}}</td><td>{{productName}}</td><td class="num">{{quantity}}</td><td class="num">{{unitPrice}}</td><td class="num">{{lineTotal}}</td></tr>{{/items}}</tbody>
    <tfoot><tr><td colspan="5">Total</td><td class="num">{{total}}</td></tr></tfoot>
  </table>
  {{#notes}}<p class="notes"><small>Notes:</small> {{notes}}</p>{{/notes}}
</section>`,
  production_report: `<section class="page">
  <div class="doc-header">
    <div><h1>Production report</h1><strong class="doc-number">{{date}}</strong></div>
    <div class="brand">{{#logoUrl}}<img src="{{logoUrl}}" alt="">{{/logoUrl}}<span>Tal-Ahwa</span></div>
  </div>
  <div class="meta"><div><small>Orders included</small><strong>{{orderCount}}</strong></div><div><small>Products</small><strong>{{productCount}}</strong></div></div>
  <table>
    <thead><tr><th>Category</th><th>SKU</th><th>Product</th><th class="num">Total quantity</th></tr></thead>
    <tbody>{{#items}}<tr><td>{{categoryName}}</td><td>{{sku}}</td><td>{{productName}}</td><td class="num">{{totalQuantity}}</td></tr>{{/items}}</tbody>
  </table>
  <p class="notes">Cancelled orders are excluded.</p>
</section>`
};

export function documentShell(title: string, body: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>${BASE_STYLES}</style></head><body>${body}</body></html>`;
}

const money = (cents: number) => `€${(cents / 100).toFixed(2)}`;
const dateOf = (value: string) => formatDate(value);
const dateTimeOf = (value: string) => formatDateTime(value);

export function deliveryNoteData(entry: DeliveryNoteEntry, logoUrl: string | null): Record<string, unknown> {
  const { note, order, shop } = entry;
  return {
    logoUrl, number: note.number, issuedDate: dateOf(note.issuedAt), orderNumber: order.orderNumber, orderDate: dateTimeOf(order.createdAt),
    shopName: order.shopName, shopAddress: shop?.address ?? "", vatNumber: shop?.vatNumber ?? "", clientName: shop?.clientName ?? "", notes: order.notes ?? "",
    totalQuantity: order.items.reduce((sum, item) => sum + item.quantity, 0),
    trayCount: order.items.filter((item) => item.isTray).reduce((sum, item) => sum + item.quantity, 0) || "",
    items: order.items.map((item) => ({ sku: item.sku ?? "—", productName: item.productName, quantity: item.quantity, unitPrice: money(item.unitPriceCents), lineTotal: money(item.lineTotalCents) }))
  };
}

/** One invoice can cover several orders: the reference lists every order number
 *  and the line items run in order sequence, so the template needs no changes. */
export function invoiceData(entry: InvoiceEntry, logoUrl: string | null): Record<string, unknown> {
  const { invoice, orders, shop } = entry;
  const first = orders[0];
  return {
    logoUrl, number: invoice.number, issuedDate: dateOf(invoice.issuedAt), dueDate: invoice.dueAt ? dateOf(invoice.dueAt) : "",
    orderNumber: orders.map((order) => order.orderNumber).join(", "), orderDate: orders.map((order) => dateTimeOf(order.createdAt)).join(", "),
    shopName: first?.shopName ?? invoice.shopName, shopAddress: shop?.address ?? "",
    vatNumber: shop?.vatNumber ?? "", clientName: shop?.clientName ?? "",
    notes: orders.map((order) => order.notes).filter(Boolean).join(" · "), total: money(invoice.totalCents),
    items: orders.flatMap((order) => order.items.map((item) => ({ sku: item.sku ?? "—", productName: item.productName, quantity: item.quantity, unitPrice: money(item.unitPriceCents), lineTotal: money(item.lineTotalCents) })))
  };
}

export function productionReportData(report: ProductionReport, logoUrl: string | null): Record<string, unknown> {
  return { logoUrl, date: report.date, orderCount: report.orderCount, productCount: report.items.length, items: report.items.map((item) => ({ categoryName: item.categoryName, sku: item.sku ?? "—", productName: item.productName, totalQuantity: item.totalQuantity })) };
}

export function printableStatementHtml(report: StatementReport): string {
  let balance = 0;
  const rows = report.rows.map((row) => { balance += row.amountCents - row.paidCents; return `<tr><td>${escapeHtml(dateOf(row.date))}</td><td>${escapeHtml(row.orderNumber)}</td><td>${escapeHtml(row.shopName)}</td><td>${escapeHtml(row.deliveryNoteNumber ?? "—")}</td><td>${escapeHtml(row.invoiceNumber ?? "—")}</td><td class="num">${money(row.amountCents)}</td><td class="num">${row.paidCents ? money(row.paidCents) : "—"}</td><td class="num">${money(balance)}</td></tr>`; }).join("");
  const body = `<section class="page">
  <div class="doc-header"><div><h1>Statement</h1><strong class="doc-number">${escapeHtml(report.scopeName)}</strong></div><div class="brand"><span>Tal-Ahwa</span></div></div>
  <div class="meta"><div><small>${report.scope === "client" ? "Client" : "Shop"}</small><strong>${escapeHtml(report.scopeName)}</strong></div><div><small>Period</small><strong>${escapeHtml(report.from)} to ${escapeHtml(report.to)}</strong></div><div><small>Orders</small><strong>${report.rows.length}</strong></div><div><small>Paid</small><strong>${money(report.paidCents)}</strong></div><div><small>Outstanding</small><strong>${money(report.outstandingCents)}</strong></div></div>
  ${report.rows.length ? `<table><thead><tr><th>Date</th><th>Order</th><th>Shop</th><th>Delivery note</th><th>Invoice</th><th class="num">Amount</th><th class="num">Paid</th><th class="num">Balance</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><td colspan="5">Total</td><td class="num">${money(report.totalCents)}</td><td class="num">${money(report.paidCents)}</td><td class="num">${money(report.outstandingCents)}</td></tr></tfoot></table>` : `<p class="notes">No orders in this period.</p>`}
</section>`;
  return documentShell("Statement", body);
}
